import { Component, OnInit, OnDestroy } from '@angular/core';
import { AsyncValidatorFn, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';

import { Observable, Subscription, map, Subject, debounceTime } from 'rxjs';

import { groupToMap } from '@shared/helpers/group-to-map';
import { SubinventoryType } from '@shared/models/subinventory';
import { saveDebounce } from '@shared/helpers/save-debounce';

import { InternalTransferSummary } from '../../../../models/shift-validation/internal-transfer';
import { ProductService } from '../../../../services/product.service';
import { ShiftContextService } from '../../../../services/shift-context.service';
import { ShiftValidationService } from '../../../../services/shift-validation/shift-validation.service';
import { ShiftContext } from '../../../../models/shift-context';
import { InternalTransfer } from '../../../../models/shift-validation/internal-transfer';

@Component({
    selector: 'app-internal-transfers',
    templateUrl: './internal-transfers.component.html',
    styleUrls: ['./internal-transfers.component.scss']
})
export class InternalTransfersComponent implements OnInit, OnDestroy {

    transfersSummary: InternalTransferSummary = { items: [] };

    groupedItems: [ string, InternalTransfer[] ][] = [];

    lastPage: boolean;
    preloadPageReady: boolean;
    loading = false;
    subscriptions: Subscription[];
    startupSubscription: Subscription;
    authorizationNumbers: FormGroup<{
        [key: string]: FormControl<string>
    }> = new FormGroup({});
    context: ShiftContext;

    private shutdownSignal = new Subject<void>();

    constructor(
        private shiftContextService: ShiftContextService,
        private shiftValidationService: ShiftValidationService,
        private productService: ProductService) { }

    ngOnInit() {
        this.loading = true;

        this.subscriptions = [
            this.shiftContextService.CurrentContext.subscribe(context => {
                this.loading = true;
                this.preloadPageReady = false;
                this.context = context;

                this.transfersSummary = this.shiftValidationService.CurrentMappedSummary<InternalTransferSummary>('InternalTransfers') ?? { items: [] };

                this.groupedItems = [ ...groupToMap(this.transfersSummary.items, i => this.getDescription(i.toSubinventoryId)).entries() ];

                Object.keys(this.authorizationNumbers.controls).forEach(c => this.authorizationNumbers.removeControl(c));

                for (const item of this.transfersSummary.items) {
                    this.authorizationNumbers.addControl(item.id.toString(), new FormControl<string>(item.authorizationNumber, [
                        Validators.required,
                        Validators.maxLength(20),
                        this.generateDuplicateValidator()
                    ], [
                        this.generateAuthNumValidator()
                    ]), {
                        emitEvent: false
                    });
                }

                this.loading = false;
            }),
            this.authorizationNumbers.statusChanges
                .pipe(debounceTime(500))
                .subscribe(status => this.shiftValidationService.SetValid(status === 'VALID')),

            this.authorizationNumbers.valueChanges
                .pipe(saveDebounce(this.shutdownSignal))
                .subscribe(() => {
                    if (this.authorizationNumbers.invalid)
                        return;

                    this.shiftValidationService
                        .updateRlcTransferAuthNumbers(this.context.id, Object.entries(this.authorizationNumbers.controls)
                            .map(o => ({
                                id: Number(o[0]),
                                authNumber: o[1].value.toString()
                            })))
                        .subscribe({
                            next: () => {
                                this.transfersSummary.items.forEach(i => {
                                    i.authorizationNumber = this.authorizationNumbers.get(i.id.toString()).value;
                                });
                            },
                            error: _ => {}
                        });
                })
        ];
    }

    ngOnDestroy(): void {
        this.shutdownSignal.next();
        this.subscriptions.forEach(s => s.unsubscribe());
        Object.keys(this.authorizationNumbers.controls).forEach(c => this.authorizationNumbers.removeControl(c));
        this.transfersSummary = { items: [] };

        this.shiftValidationService.SetValid(true);
    }

    getDescription(toSubinvId: number): string {
        const toSubinv = this.context.site.subinventories.find(x => x.id === toSubinvId);

        if (toSubinv?.subinventoryType === SubinventoryType.ChepPlantPallets)
            return 'RAD Transfers';
        else if (toSubinv?.subinventoryType === SubinventoryType.VendorOnPremise)
            return 'VOP Transfers';
        else if (toSubinv?.subinventoryType === SubinventoryType.PecoPallets)
            return 'PECO Transfers';
        else if (toSubinv?.subinventoryType === SubinventoryType.Rm2Pallets)
            return 'RM2 Transfers';
        else
            return 'Other Transfers';
    }

    generateDuplicateValidator(): ValidatorFn {
        return (control: FormControl<string>): ValidationErrors | null => Object.values(this.authorizationNumbers.controls)
            .some(a => a.value === control.value && a !== control)
                ? { 'dupAuth': true }
                : null;
    }

    generateAuthNumValidator(): AsyncValidatorFn {
        return (control: FormControl<string>): Observable<ValidationErrors | null> => this.shiftValidationService
            .isAuthNumberInUse(this.context.id, control.value)
            .pipe(map(inUse => inUse ? { 'authInUse': true } : null));
    }

    getProduct(productId: number): Observable<string> {
        return this.productService.FindProduct(productId).pipe(map(p => p?.label));
    }

    sumTotal(selector: string | ((item: InternalTransfer) => number), key: string): number {
        const func = typeof selector === 'string'
            ? (row: InternalTransfer) => row[selector]
            : selector;

        return (this.transfersSummary.items ?? [])
            .filter(i => this.getDescription(i.toSubinventoryId) === key)
            .map(func)
            .reduce((agg, cur) => agg + cur, 0);
    }
}
